import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import api from '../services/api';

const ReleaseComplianceWidget = ({ selectedTab, selectedWeek, selectedMonth }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gapData, setGapData] = useState(null);
  const [expandedChannels, setExpandedChannels] = useState({});

  useEffect(() => {
    fetchReleaseGaps();
  }, [selectedTab, selectedWeek, selectedMonth]);

  const fetchReleaseGaps = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Build params based on Dashboard filter selection
      const params = {};
      
      if (selectedTab === 0) {
        // Weekly view - pass week offset
        params.weekOffset = selectedWeek;
      } else {
        // Monthly view - pass year and month
        const [year, month] = selectedMonth.split('-');
        params.year = year;
        params.month = month;
      }
      
      const response = await api.get('/content/release-gaps', { params });
      
      setGapData(response.data.data);
      
      // Auto-expand channels with warnings or critical status
      const autoExpand = {};
      response.data.data.channels.forEach((channel, index) => {
        if (channel.complianceStatus === 'warning' || channel.complianceStatus === 'critical') {
          autoExpand[index] = true;
        }
      });
      setExpandedChannels(autoExpand);
    } catch (err) {
      console.error('Error fetching release gaps:', err);
      setError(err.response?.data?.message || 'Failed to load release gap data');
    } finally {
      setLoading(false);
    }
  };

  const toggleChannel = (index) => {
    setExpandedChannels(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />;
      case 'warning':
        return <WarningIcon sx={{ color: 'warning.main', fontSize: 20 }} />;
      case 'critical':
        return <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />;
      case 'no_data':
        return <InfoIcon sx={{ color: 'grey.500', fontSize: 20 }} />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return '#e8f5e9'; // Very light green
      case 'warning':
        return '#fff3e0'; // Very light orange
      case 'critical':
        return '#ffebee'; // Very light red
      case 'no_data':
        return 'grey.100';
      default:
        return 'background.paper';
    }
  };

  const getGapChipColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'success';
      case 'warning':
        return 'warning';
      case 'critical':
        return 'error';
      case 'first':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: { xs: '1rem', md: '1.25rem' } }}>
            📺 {isMobile ? 'LV Rel Compliance' : 'Long Video Release Compliance'}
          </Typography>
          {gapData && (
            <Typography variant="caption" color="text.secondary">
              {gapData.monthLabel}
            </Typography>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : gapData ? (
          <>
            {/* Summary Cards */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              {gapData.summary.healthyChannels > 0 && (
                <Chip 
                  label={`${gapData.summary.healthyChannels} Healthy`}
                  color="success"
                  size="small"
                  variant="outlined"
                />
              )}
              {gapData.summary.warningChannels > 0 && (
                <Chip 
                  label={`${gapData.summary.warningChannels} Warning`}
                  color="warning"
                  size="small"
                  variant="outlined"
                />
              )}
              {gapData.summary.criticalChannels > 0 && (
                <Chip 
                  label={`${gapData.summary.criticalChannels} Critical`}
                  color="error"
                  size="small"
                  variant="outlined"
                />
              )}
              {gapData.summary.noDataChannels > 0 && (
                <Chip 
                  label={`${gapData.summary.noDataChannels} No Data`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Channel List with Expandable Details */}
            <Box sx={{ maxHeight: 600, overflowY: 'auto' }}>
              {gapData.channels.map((channel, index) => (
                <Box 
                  key={index}
                  sx={{ mb: 2 }}
                >
                  <Box
                    sx={{ 
                      p: 1.5,
                      borderRadius: 3,
                      background: channel.complianceStatus === 'critical' 
                        ? 'linear-gradient(135deg, #ffc3f0 0%, #ffb3ba 100%)'
                        : channel.complianceStatus === 'warning'
                        ? 'linear-gradient(135deg, #ffe5d9 0%, #fff0f5 100%)'
                        : channel.complianceStatus === 'no_data'
                        ? 'linear-gradient(135deg, #f0f0f0 0%, #fafafa 100%)'
                        : 'linear-gradient(135deg, #d4f1f4 0%, #ffe5f0 100%)',
                      cursor: channel.totalReleases > 0 ? 'pointer' : 'default',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: channel.totalReleases > 0 ? 'translateY(-2px)' : 'none',
                        boxShadow: channel.totalReleases > 0 ? '0 4px 12px rgba(0,0,0,0.15)' : '0 2px 8px rgba(0,0,0,0.1)'
                      }
                    }}
                    onClick={() => channel.totalReleases > 0 && toggleChannel(index)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getStatusIcon(channel.complianceStatus)}
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        {channel.channelName}
                      </Typography>
                      
                      <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                        {channel.statusMessage}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {channel.target !== null && (
                          <Chip 
                            label={isMobile 
                              ? `Tgt: ${channel.target}, Act: ${channel.actual}`
                              : `Target: ${channel.target}, Actual: ${channel.actual}`
                            }
                            size="small"
                            sx={{ 
                              bgcolor: 'white',
                              fontWeight: 'bold'
                            }}
                          />
                        )}
                        {channel.totalReleases > 0 && (
                          <>
                            {!isMobile && channel.maxGap !== null && (
                              <Chip 
                                label={`Max: ${channel.maxGap}d`}
                                size="small"
                                sx={{ bgcolor: 'white' }}
                              />
                            )}
                            {!isMobile && channel.avgGap !== null && (
                              <Chip 
                                label={`Avg: ${channel.avgGap}d`}
                                size="small"
                                sx={{ bgcolor: 'white' }}
                              />
                            )}
                            <IconButton size="small">
                              {expandedChannels[index] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  {/* Expandable Release Details */}
                  {channel.totalReleases > 0 && (
                    <Collapse in={expandedChannels[index]} timeout="auto" unmountOnExit>
                      <Box sx={{ mt: 1, ml: 2 }}>
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ bgcolor: 'grey.100' }}>
                                {!isMobile && <TableCell><strong>#</strong></TableCell>}
                                <TableCell sx={{ wordWrap: 'break-word', maxWidth: isMobile ? '150px' : 'none' }}><strong>Video Title</strong></TableCell>
                                <TableCell align="center"><strong>Release Date</strong></TableCell>
                                <TableCell align="center"><strong>Gap</strong></TableCell>
                                {!isMobile && <TableCell><strong>Comment</strong></TableCell>}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {channel.releases.map((release, rIndex) => (
                                <TableRow 
                                  key={rIndex}
                                  sx={{
                                    bgcolor: release.gapStatus === 'critical' ? '#ffebee' : // Very light red
                                             release.gapStatus === 'warning' ? '#fff3e0' : // Very light orange
                                             'background.paper'
                                  }}
                                >
                                  {!isMobile && <TableCell>{rIndex + 1}</TableCell>}
                                  <TableCell sx={{ wordWrap: 'break-word', maxWidth: isMobile ? '150px' : 'none' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
                                      {release.title}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                                      {formatDate(release.releaseDate)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    {release.gapFromPrevious !== null ? (
                                      <Chip 
                                        label={`${release.gapFromPrevious}d`}
                                        size="small"
                                        color={getGapChipColor(release.gapStatus)}
                                        sx={{ minWidth: 45 }}
                                      />
                                    ) : (
                                      <Chip 
                                        label="-"
                                        size="small"
                                        sx={{ minWidth: 45 }}
                                      />
                                    )}
                                  </TableCell>
                                  {!isMobile && (
                                    <TableCell>
                                      <Typography variant="caption" color="text.secondary">
                                        {release.gapComment}
                                      </Typography>
                                    </TableCell>
                                  )}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    </Collapse>
                  )}
                </Box>
              ))}
            </Box>

            {/* Footer */}
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Period: {formatDate(gapData.periodStart)} - {formatDate(gapData.periodEnd)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Releases: {gapData.summary.totalReleases}
              </Typography>
            </Box>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default ReleaseComplianceWidget;